import React from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import BottomSheetDialog from './mobile-bottom-sheet';
import DeletePopupMobile from './delete-mobile';

interface MatchingTransaction {
    date: string;
    description: string;
    amount: number;
    isSource?: boolean;
    guid?: string;
}

interface PatternAnalysisData {
    simplifiedDescription?: string;
    interval?: string;
    medianDays?: number;
    matchedRange?: [number, number];
    matchingTransactions?: MatchingTransaction[];
    startDate?: string;
    usedTolerancePercent?: number;
    amount?: number;
    message?: string;
    cutoffWarning?: { shouldWarn: boolean; reason?: string } | null;
    suggestedCategoryId?: number;
    comparisonType?: string;
}

interface PatternAnalysisDisplayProps {
    open: boolean;
    analysisData: PatternAnalysisData | null;
    onClose: () => void;
    onContinue: (sourceTransactionGuid?: string) => void;
    transactionGuid?: string;
    categories?: any[];
    isDescriptionOnlyMatch?: boolean;
    onReanalyze?: (sourceTransactionGuid?: string) => void;
    onReanalyzeByAI?: (sourceTransactionGuid?: string) => void;
    showReanalyzeButton?: boolean;
    showReanalyzeByAIButton?: boolean;
}

const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try { return format(new Date(dateString), 'MMM dd, yyyy'); } catch { return dateString; }
};

const formatAmount = (amount?: number) => {
    if (amount === null || amount === undefined) return 'N/A';
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount); } catch { return String(amount); }
};

const getCategoryName = (categoryId: number | undefined, categories: any[] | undefined): string => {
    if (!categoryId || !categories) return 'N/A';
    const findCategory = (cats: any[]): any => {
        for (const cat of cats) {
            if (cat.id === categoryId) return cat;
            if (cat.children) {
                const found = findCategory(cat.children);
                if (found) return found;
            }
        }
        return null;
    };
    const found = findCategory(categories);
    return found ? found.name : 'N/A';
};

const TxCard: React.FC<{ tx: MatchingTransaction; highlight?: boolean }> = ({ tx, highlight }) => (
    <Box sx={{ p: 1.25, borderRadius: 1, border: '1px solid #e0e0e0', background: highlight ? '#eafceb' : 'var(--color-container-background)', display: 'flex', justifyContent: 'space-between' }}>
        <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatDate(tx?.date)}</Typography>
            <Typography sx={{ fontSize: 12, color: '#707070' }}>{tx?.description || 'N/A'}</Typography>
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: (tx?.amount ?? 0) < 0 ? 'var(--color-negative-amount)' : '#388e3c' }}>
            {formatAmount(tx?.amount)}
        </Typography>
    </Box>
);

// Bottom-sheet phone equivalent of pattern-analysis-display.tsx - same stat
// summary, source/matching transaction lists as cards instead of tables.
const PatternAnalysisDisplayMobile: React.FC<PatternAnalysisDisplayProps> = ({
    open,
    analysisData,
    onClose,
    onContinue,
    transactionGuid,
    categories,
    isDescriptionOnlyMatch,
    onReanalyze,
    onReanalyzeByAI,
    showReanalyzeButton,
    showReanalyzeByAIButton
}) => {
    const [showCutoffConfirm, setShowCutoffConfirm] = React.useState(false);

    if (!analysisData) return null;

    const handleContinue = () => {
        if (analysisData?.cutoffWarning?.shouldWarn) {
            setShowCutoffConfirm(true);
            return;
        }
        onContinue(transactionGuid);
    };

    const matches = (analysisData.matchingTransactions || []).filter(tx => !tx.isSource);
    const sources = (analysisData.matchingTransactions || []).filter(tx => tx.isSource);

    return (
        <>
            <BottomSheetDialog open={open} onClose={onClose}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1, borderBottom: '1px solid #e0e0e0' }}>
                    <IconButton onClick={onClose}><ArrowLeft size={22} /></IconButton>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, flex: 1 }}>Pattern Analysis Results</Typography>
                    {analysisData.message && (
                        <Box sx={{ color: '#fff', textTransform: 'uppercase', background: 'red', borderRadius: 1, px: 1, py: 0.25, fontSize: 10, fontWeight: 700 }}>
                            Not recurring
                        </Box>
                    )}
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>Analysis Summary</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                            <Box>
                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Description</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{analysisData.simplifiedDescription || 'N/A'}</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Interval</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{analysisData.interval || 'N/A'}</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Median Days</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{analysisData.medianDays ?? 'N/A'}</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Amount</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatAmount(analysisData.amount)}</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Next Occurrence</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatDate(analysisData.startDate)}</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Matched Range (days)</Typography>
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                                    {analysisData.matchedRange ? `${analysisData.matchedRange[0]} - ${analysisData.matchedRange[1]}` : 'N/A'}
                                </Typography>
                            </Box>
                            {analysisData.suggestedCategoryId !== undefined && (
                                <Box>
                                    <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Suggested Category</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{getCategoryName(analysisData.suggestedCategoryId, categories)}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {matches.length > 0 && (
                        <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>
                                Matching Transaction(s){' '}
                                <Box component="span" sx={{ color: 'var(--color-primary)', fontWeight: 500, fontSize: 12 }}>
                                    {isDescriptionOnlyMatch ? '(Description Only)' : (analysisData.comparisonType === 'ai' ? '(AI Analysis)' : `(Full Analysis at ${analysisData.usedTolerancePercent ?? 0}% tolerance)`)}
                                </Box>
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {matches.map((tx, idx) => <TxCard key={idx} tx={tx} />)}
                            </Box>
                        </Box>
                    )}
                    {analysisData.matchingTransactions?.some(tx => !tx.isSource) && matches.length === 0 && (
                        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>No matching transactions found</Typography>
                    )}

                    {sources.length > 0 && (
                        <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>Source Transaction(s)</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {sources.map((tx, idx) => <TxCard key={idx} tx={tx} highlight />)}
                            </Box>
                        </Box>
                    )}
                </Box>
                <Box sx={{ borderTop: '1px solid #e0e0e0', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, position: 'sticky', bottom: 0, background: 'var(--color-page-background)' }}>
                    <Button onClick={handleContinue} variant="contained" color="primary" fullWidth sx={{ color: '#fff' }}>
                        Continue
                    </Button>
                    {showReanalyzeButton && onReanalyze && (
                        <Button
                            onClick={() => onReanalyze(transactionGuid)}
                            variant="outlined"
                            fullWidth
                            sx={{ borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}
                        >
                            Reanalyze by Description
                        </Button>
                    )}
                    {showReanalyzeByAIButton && onReanalyzeByAI && (
                        <Button
                            onClick={() => onReanalyzeByAI(transactionGuid)}
                            variant="outlined"
                            fullWidth
                            sx={{ borderColor: '#7C3AED', color: '#7C3AED' }}
                        >
                            Reanalyze by AI
                        </Button>
                    )}
                    <Button onClick={onClose} variant="outlined" color="primary" fullWidth>
                        Cancel
                    </Button>
                </Box>
            </BottomSheetDialog>
            <DeletePopupMobile
                open={showCutoffConfirm}
                message="This recurring charge may have stopped"
                description={analysisData?.cutoffWarning?.reason || 'The pattern looks stale compared to the most recent activity on this account.'}
                confirmLabel="Provision Anyway"
                cancelLabel="Cancel"
                onCancel={() => setShowCutoffConfirm(false)}
                onConfirm={() => {
                    setShowCutoffConfirm(false);
                    onContinue(transactionGuid);
                }}
            />
        </>
    );
};

export default PatternAnalysisDisplayMobile;
